import React, { Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { Layout } from '../Layout/Layout'
import { MdHome, MdPerson2, MdMoney, MdGroup, MdBook, MdStickyNote2, MdCheckCircle, MdAccountBalanceWallet, MdCompareArrows, MdLabel } from 'react-icons/md'
import { ErrorBoundary } from '../../pages/ErrorBoundary'
import { UserRole } from '@/ts/interfaces/interfaces'
import { RouteLoading } from './RouteLoading'
import { lazyWithRetry } from '../../utils/chunkLoadError'

// Lazy load all page components with retry
const HomePage = lazyWithRetry(() => import('../../pages/NewHome').then((m) => ({ default: m.NewHomePage })))
const Status404 = lazyWithRetry(() => import('../../pages/404').then((m) => ({ default: m.Status404 })))
const SignUp = lazyWithRetry(() => import('../../pages/SignUp').then((m) => ({ default: m.SignUp })))
const SignIn = lazyWithRetry(() => import('../../pages/SignIn').then((m) => ({ default: m.SignIn })))
const AccessRequestActionPage = lazyWithRetry(() => import('../../pages/AccessRequestActionPage').then((m) => ({ default: m.AccessRequestActionPage })))
const AccessRequestPage = lazyWithRetry(() => import('../../pages/AccessRequestPage').then((m) => ({ default: m.AccessRequestPage })))
const OwnAccountPage = lazyWithRetry(() => import('../../pages/OwnAccountPage').then((m) => ({ default: m.OwnAccountPage })))
const PricingPage = lazyWithRetry(() => import('../../pages/PricingPage').then((m) => ({ default: m.PricingPage })))
const BusinessDashboardPage = lazyWithRetry(() => import('../../pages/BusinessDashboardPage').then((m) => ({ default: m.BusinessDashboardPage })))
const UsersDashboardPage = lazyWithRetry(() => import('../../pages/UsersDashboardPage').then((m) => ({ default: m.UsersDashboardPage })))
const ProfilePage = lazyWithRetry(() => import('../../pages/ProfilePage').then((m) => ({ default: m.ProfilePage })))
const UpdateTaxInformationPage = lazyWithRetry(() => import('../../pages/UpdateTaxInformationFormPage').then((m) => ({ default: m.UpdateTaxInformationPage })))
const CreateTaxInformationPage = lazyWithRetry(() => import('../../pages/CreateTaxInformationPage').then((m) => ({ default: m.CreateTaxInformationPage })))
const OtpRecoverPasswordPage = lazyWithRetry(() => import('../../pages/OtpRecoverPasswordPage').then((m) => ({ default: m.OtpRecoverPasswordPage })))
const OtpRenewPasswordPage = lazyWithRetry(() => import('../../pages/OtpRenewPasswordPage').then((m) => ({ default: m.OtpRenewPasswordPage })))
const ReceiptsDashboardPage = lazyWithRetry(() => import('../../pages/ReceiptsDashboard').then((m) => ({ default: m.ReceiptsDashboardPage })))
const ApprovalsDashboardPage = lazyWithRetry(() => import('../../pages/ApprovalsDashboard').then((m) => ({ default: m.ApprovalsDashboardPage })))
const ReceiptDetails = lazyWithRetry(() => import('../../pages/ReceiptDetails').then((m) => ({ default: m.ReceiptDetails })))
const AddUserPage = lazyWithRetry(() => import('../../pages/AddUserPage').then((m) => ({ default: m.AddUserPage })))
const WelcomeSubscriptionPage = lazyWithRetry(() => import('../../pages/WelcomeSubscriptionPage').then((m) => ({ default: m.WelcomeSubscriptionPage })))
const UpdateUserPage = lazyWithRetry(() => import('../../pages/UpdateUserPage').then((m) => ({ default: m.UpdateUserPage })))
const UpdateBusinessPage = lazyWithRetry(() => import('../../pages/UpdateBusinessPage').then((m) => ({ default: m.UpdateBusinessPage })))
const CreateBusinessPage = lazyWithRetry(() => import('../../pages/CreateBusinessPage').then((m) => ({ default: m.CreateBusinessPage })))
const TagsPage = lazyWithRetry(() => import('../../pages/TagsPage').then((m) => ({ default: m.TagsPage })))
const BudgetsDashboardPage = lazyWithRetry(() => import('../../pages/BudgetsDashboardPage').then((m) => ({ default: m.BudgetsDashboardPage })))
const ConciliationDashboard = lazyWithRetry(() => import('../../pages/Conciliation/ConciliationDashboard').then((m) => ({ default: m.ConciliationDashboard })))
const StatementDetail = lazyWithRetry(() => import('../../pages/Conciliation/StatementDetail').then((m) => ({ default: m.StatementDetail })))

export type RouterObj = {
  label?: string | Record<UserRole, string> | null
  path: string
  element: any
  cleanParamsPath?: string
  icon?: React.ElementType | null
}
export const routerObj: Record<string, RouterObj> = {
  signUp: {
    path: '/sign-up',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <SignUp />
      </Suspense>
    )
  },
  signIn: {
    path: '/sign-in',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <SignIn />
      </Suspense>
    )
  },
  requestAccess: {
    path: '/request-access',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <AccessRequestPage />
      </Suspense>
    )
  },
  accessRequestAction: {
    path: '/access-request',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <AccessRequestActionPage />
      </Suspense>
    )
  },
  accessRequestForm: {
    path: '/new-access-request',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <AccessRequestPage />
      </Suspense>
    )
  },
  accessRequestOwnAccount: {
    path: '/access-request/own-account',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <OwnAccountPage />
      </Suspense>
    )
  },
  home: {
    path: '/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <HomePage />
      </Suspense>
    ),
    icon: MdHome,
    label: 'nav.home'
  },
  pricing: {
    path: '/pricing',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <PricingPage />
      </Suspense>
    ),
    icon: MdMoney,
    label: 'nav.pricing'
  },
  createTaxInformation: {
    path: '/tax-information/create',
    cleanParamsPath: '/tax-information/create',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <CreateTaxInformationPage />
      </Suspense>
    )
  },
  createBusiness: {
    path: '/business/create',
    cleanParamsPath: '/business/create',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <CreateBusinessPage />
      </Suspense>
    )
  },
  updateTaxInformation: {
    path: '/tax-information/update/:id',
    cleanParamsPath: '/tax-information/update',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <UpdateTaxInformationPage />
      </Suspense>
    )
  },
  businessDashboard: {
    path: '/business/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <BusinessDashboardPage />
      </Suspense>
    ),
    icon: MdBook,
    label: 'nav.businesses'
  },
  updateBusiness: {
    path: '/business/update/:id',
    cleanParamsPath: '/business/update',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <UpdateBusinessPage />
      </Suspense>
    )
  },
  usersDashboard: {
    path: '/user/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <UsersDashboardPage />
      </Suspense>
    ),
    icon: MdGroup,
    label: 'nav.users'
  },
  AddUser: {
    path: '/user/create',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <AddUserPage />
      </Suspense>
    )
  },
  UpdateUser: {
    path: '/user/update/:id',
    cleanParamsPath: '/user/update',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <UpdateUserPage />
      </Suspense>
    )
  },
  expensesDashboard: {
    path: '/expenses/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <ReceiptsDashboardPage />
      </Suspense>
    ),
    icon: MdStickyNote2,
    label: {
      [UserRole.ADMIN]: 'nav.expenses',
      [UserRole.REVIEWER]: 'nav.myExpenses',
      [UserRole.EMPLOYEE]: 'nav.myExpenses'
    }
  },
  approvalsDashboard: {
    path: '/expenses/approvals',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <ApprovalsDashboardPage />
      </Suspense>
    ),
    icon: MdCheckCircle,
    label: 'nav.approvals'
  },
  budgetsDashboard: {
    path: '/budgets/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <BudgetsDashboardPage />
      </Suspense>
    ),
    icon: MdAccountBalanceWallet,
    label: 'nav.budgets'
  },
  profile: {
    path: '/user/profile',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <ProfilePage />
      </Suspense>
    ),
    icon: MdPerson2,
    label: 'nav.profile'
  },
  renewPassword: {
    path: '/renew-password/',
    cleanParamsPath: '/renew-password',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <OtpRenewPasswordPage />
      </Suspense>
    )
  },
  recoverPassword: {
    path: '/recover-password/',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <OtpRecoverPasswordPage />
      </Suspense>
    )
  },
  receiptDetails: {
    path: '/receipt/:id',
    cleanParamsPath: '/receipt',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <ReceiptDetails />
      </Suspense>
    )
  },
  welcomeSubscriptionPage: {
    path: '/welcome',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <WelcomeSubscriptionPage />
      </Suspense>
    )
  },
  conciliationDashboard: {
    path: '/conciliations/dashboard',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <ConciliationDashboard />
      </Suspense>
    ),
    icon: MdCompareArrows,
    label: 'nav.conciliation'
  },
  statementDetail: {
    path: '/conciliations/statements/:id',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <StatementDetail />
      </Suspense>
    )
  },
  settings: {
    path: '/settings',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <TagsPage />
      </Suspense>
    ),
    icon: MdLabel,
    label: 'nav.tags'
  },
  error404: {
    path: '*',
    element: (
      <Suspense fallback={<RouteLoading />}>
        <Status404 />
      </Suspense>
    )
  }
}

export const ADMIN_LAYOUT = [routerObj.home, routerObj.profile, routerObj.pricing, routerObj.businessDashboard, routerObj.usersDashboard, routerObj.expensesDashboard, routerObj.approvalsDashboard, routerObj.budgetsDashboard, routerObj.conciliationDashboard, routerObj.settings]

export const REVIEWER_LAYOUT = [routerObj.profile, routerObj.expensesDashboard, routerObj.approvalsDashboard]

export const EMPLOYEE_LAYOUT = [routerObj.profile, routerObj.expensesDashboard]

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary>
        <Layout />
      </ErrorBoundary>
    ),
    children: [
      {
        path: routerObj.signUp.path,
        element: routerObj.signUp.element
      },
      {
        path: routerObj.signIn.path,
        element: routerObj.signIn.element
      },
      {
        path: routerObj.requestAccess.path,
        element: routerObj.requestAccess.element
      },
      {
        path: routerObj.accessRequestAction.path,
        element: routerObj.accessRequestAction.element
      },
      {
        path: routerObj.accessRequestForm.path,
        element: routerObj.accessRequestForm.element
      },
      {
        path: routerObj.accessRequestOwnAccount.path,
        element: routerObj.accessRequestOwnAccount.element
      },
      {
        path: routerObj.home.path,
        element: routerObj.home.element
      },
      {
        path: routerObj.pricing.path,
        element: routerObj.pricing.element
      },
      {
        path: routerObj.businessDashboard.path,
        element: routerObj.businessDashboard.element
      },
      {
        path: routerObj.usersDashboard.path,
        element: routerObj.usersDashboard.element
      },
      {
        path: routerObj.profile.path,
        element: routerObj.profile.element
      },
      {
        path: routerObj.renewPassword.path,
        element: routerObj.renewPassword.element
      },
      {
        path: routerObj.recoverPassword.path,
        element: routerObj.recoverPassword.element
      },
      {
        path: routerObj.updateTaxInformation.path,
        element: routerObj.updateTaxInformation.element
      },
      {
        path: routerObj.updateBusiness.path,
        element: routerObj.updateBusiness.element
      },
      {
        path: routerObj.createTaxInformation.path,
        element: routerObj.createTaxInformation.element
      },
      {
        path: routerObj.createBusiness.path,
        element: routerObj.createBusiness.element
      },
      {
        path: routerObj.AddUser.path,
        element: routerObj.AddUser.element
      },
      {
        path: routerObj.UpdateUser.path,
        element: routerObj.UpdateUser.element
      },
      {
        path: routerObj.expensesDashboard.path,
        element: routerObj.expensesDashboard.element
      },
      {
        path: routerObj.approvalsDashboard.path,
        element: routerObj.approvalsDashboard.element
      },
      {
        path: routerObj.budgetsDashboard.path,
        element: routerObj.budgetsDashboard.element
      },
      {
        path: routerObj.receiptDetails.path,
        element: routerObj.receiptDetails.element
      },
      {
        path: routerObj.welcomeSubscriptionPage.path,
        element: routerObj.welcomeSubscriptionPage.element
      },
      {
        path: routerObj.conciliationDashboard.path,
        element: routerObj.conciliationDashboard.element
      },
      {
        path: routerObj.statementDetail.path,
        element: routerObj.statementDetail.element
      },
      {
        path: routerObj.settings.path,
        element: routerObj.settings.element
      }
    ]
  }
])

const Router = () => {
  return <RouterProvider router={router} />
}

export { Router }
